import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import { memo, useCallback, useContext } from 'react';

import { useOptimisticState } from '@/hooks/useOptimisticState';
import { UserSettingsContext } from '@/spaces/settings/UserSettingsContext';

import type { IGCommentWatcherClassContent } from '../../../../bridge/types/artifact.ts';

import { AvatarCircle } from './AvatarCircle';
import {
	CollapsiblePrompt,
	MutedLabel,
} from './comment-watcher/CollapsiblePrompt';
import type { ArtifactComponentProps } from './types';

type CommentReplyProps = ArtifactComponentProps<IGCommentWatcherClassContent>;

const exampleBubbleBackground = style({
	backgroundColor: 'informative-200',
});

export const CommentReply = memo(function CommentReply({
	content,
	isReadOnly = false,
	onContentChange,
}: CommentReplyProps) {
	const settings = useContext(UserSettingsContext)?.settings;
	const [promptValue, setPromptValue] = useOptimisticState(
		content.reply_prompt ?? '',
	);
	const businessUsername =
		settings?.instagram_connection?.ig_username ||
		settings?.instagram_handle ||
		'yourbusiness';

	const handlePromptCommit = useCallback(
		(value: string) => onContentChange?.({ reply_prompt: value }),
		[onContentChange],
	);

	return (
		<div className="flex flex-col gap-2.5">
			<div>
				<MutedLabel>Prompt</MutedLabel>
				<div className="mt-1">
					<CollapsiblePrompt
						value={promptValue}
						isReadOnly={isReadOnly}
						onChange={setPromptValue}
						onCommit={(value) => {
							if (value !== (content.reply_prompt ?? '')) {
								handlePromptCommit(value);
							}
						}}
					/>
				</div>
			</div>

			{content.example_reply && (
				<div className="py-1">
					<MutedLabel>Example reply</MutedLabel>
					<div className="mt-1.5 flex items-start gap-1.5">
						<svg
							width="16"
							height="14"
							viewBox="0 0 16 14"
							fill="none"
							className="mt-1.5 shrink-0"
							aria-hidden
						>
							<path
								d="M2 0 V7 Q2 10 5 10 H13"
								stroke="rgba(0,0,0,0.18)"
								strokeWidth="1.5"
								fill="none"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<polyline
								points="10,7 13,10 10,13"
								stroke="rgba(0,0,0,0.18)"
								strokeWidth="1.5"
								fill="none"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
						<div className="mt-px shrink-0">
							<AvatarCircle username={businessUsername} />
						</div>
						<div className="min-w-0">
							<span
								className="block text-[0.6875rem] leading-[1.3] font-semibold
									text-(--text-primary)"
							>
								{businessUsername}
							</span>
							<div
								className={`${exampleBubbleBackground} mt-[3px] inline-block
								max-w-full rounded-[0_8px_8px_8px] px-[9px] py-1`}
							>
								<p className="m-0 text-xs leading-[1.4] text-(--text-secondary)">
									{content.example_reply}
								</p>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
});
